import { PropsWithChildren, useEffect, useState } from 'react';
import { startVersionGate, VersionGateResult } from 'services/nativeVersionGate';

import UpdateRequiredScreen from './UpdateRequiredScreen';

/**
 * Decides, once per cold start, whether to show the terminal update-required
 * screen instead of the app.
 *
 * Nothing renders until the verdict is in, so a player on an unsupported
 * build never glimpses the app behind the block. An earlier version let
 * children render while the request was in flight, on the reasoning that
 * holding boot would tax every launch; that reasoning was wrong. The check is
 * a small CDN document read over native http, started at module scope in
 * index.tsx BEFORE the first render, so it overlaps React boot and has almost
 * always resolved by the time this mounts. The 1.5s cap in the service is the
 * worst case on a bad network, not the normal cost.
 *
 * While pending this renders nothing, which shows the dark page background
 * (see index.css) — the same colour as the native launch screen, so the hold
 * is continuous rather than a white flash.
 *
 * Cold start only: the check is memoised per app start and there is no
 * lifecycle listener, so raising the floor while a player is at a table
 * cannot interrupt them. They see it on their next cold start.
 */
const NativeVersionGate = ({ children }: PropsWithChildren) => {
  const [result, setResult] = useState<VersionGateResult | null>(null);

  useEffect(() => {
    let active = true;
    startVersionGate()
      .then((next) => {
        if (active) setResult(next);
      })
      // startVersionGate already resolves rather than rejects on every known
      // failure, but an unexpected throw must not leave the gate holding
      // forever with a blank screen.
      .catch(() => {
        if (active) setResult({ blocked: false, reason: 'unreachable' });
      });
    return () => {
      active = false;
    };
  }, []);

  // Hold until the verdict is known.
  if (!result) return null;

  if (result.blocked) {
    return (
      <UpdateRequiredScreen
        downloadUrl={result.downloadUrl}
        installed={result.installed}
        required={result.required}
      />
    );
  }

  return <>{children}</>;
};

export default NativeVersionGate;
